import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FolderOpen, Edit3, Eye, ArrowRightLeft, Settings2, Wrench, HelpCircle,
  ChevronDown, Save, Download, FileText, Calendar, Printer,
  Undo2, Clipboard, Sparkles, BookOpen, Users, Building2, UserCircle,
  Search, CreditCard, Wallet, Receipt, DollarSign, Clock,
  Calculator, ScanLine, Stethoscope, AlertTriangle, BarChart3,
  BadgePercent, Briefcase, Truck, FileCheck, ClipboardList, BookMarked, Shield, Lock, Upload
} from 'lucide-react';
import { useLicense } from '../../context/LicenseContext';

const HorizontalNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const { isPathLicensed } = useLicense();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/',
    },
    {
      id: 'file',
      label: 'File',
      icon: FolderOpen,
      children: [
        { label: 'Open / Close Books', icon: BookOpen, path: '/file/books' },
        { label: 'Backup & Restore', icon: Save, path: '/file/backup' },
        { label: 'Import / Export', icon: Download, path: '/file/import-export' },
        { label: 'Import All Existing Data', icon: Upload, path: '/file/import-all' },
        { label: 'Report Center', icon: Printer, path: '/file/reports' },
        { label: 'Custom Report Builder', icon: Sparkles, path: '/custom-reports', isAI: true },
        { label: 'Year-End / Period Close', icon: Calendar, path: '/file/year-end' },
      ]
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: Edit3,
      children: [
        { label: 'Undo / Redo', icon: Undo2, path: '/edit/history' },
        { label: 'Clipboard Viewer', icon: Clipboard, path: '/edit/clipboard' },
        { label: 'AI Fix Formatting', icon: Sparkles, path: '/edit/ai-format', isAI: true },
      ]
    },
    {
      id: 'view',
      label: 'File Maintenance',
      icon: Eye,
      children: [
        { label: 'Chart of Accounts', icon: BookOpen, path: '/view/chart-of-accounts' },
        { label: 'Vendors', icon: Building2, path: '/view/vendors' },
        { label: 'Customers', icon: Users, path: '/view/customers' },
        { label: 'Employees', icon: UserCircle, path: '/view/employees' },
        { label: 'Enterprises', icon: Briefcase, path: '/view/enterprises' },
        { label: 'Equipment', icon: Truck, path: '/view/equipment' },
        { isDivider: true },
        { label: 'Reports & Analytics', icon: FileText, path: '/reports' },
        { label: 'Transaction Browser', icon: Search, path: '/view/transactions' },
        { label: 'AI Smart Lookup', icon: Sparkles, path: '/view/smart-lookup', isAI: true },
      ]
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: ArrowRightLeft,
      children: [
        { label: 'Quick Check', icon: FileCheck, path: '/transactions/quick-check' },
        { label: 'Accounts Payable', icon: CreditCard, path: '/transactions/ap' },
        { label: 'Deposits', icon: Wallet, path: '/transactions/deposits' },
        { label: 'Accounts Receivable', icon: Receipt, path: '/transactions/ar' },
        { isDivider: true },
        { label: 'Paychecks', icon: DollarSign, path: '/transactions/paychecks' },
        { label: 'Timecards', icon: Clock, path: '/transactions/timecards' },
        { label: 'Void Checks', icon: AlertTriangle, path: '/transactions/void-checks' },
        { isDivider: true },
        { label: 'Journal Entries', icon: BookMarked, path: '/transactions/journal' },
        { label: 'Posting Queue', icon: ClipboardList, path: '/transactions/posting-queue' },
      ]
    },
    {
      id: 'setup',
      label: 'Setup',
      icon: Settings2,
      children: [
        { label: 'Company Setup', icon: Building2, path: '/setup/company' },
        { label: 'Chart of Accounts', icon: BookOpen, path: '/setup/accounts' },
        { isDivider: true },
        { label: 'Tax Setup', icon: BadgePercent, path: '/tax-setup', isHighlight: true },
        { label: 'Payroll', icon: DollarSign, path: '/payroll', isHighlight: true },
        { label: 'Payroll Reports', icon: FileText, path: '/payroll-reports', isHighlight: true },
        { label: 'Financial Reports', icon: BarChart3, path: '/financial-reports', isHighlight: true },
        { label: 'Cost Accounting', icon: Briefcase, path: '/cost-accounting', isHighlight: true },
        { isDivider: true },
        { label: 'Legacy: Tax & Payroll', icon: BadgePercent, path: '/setup/tax-tables' },
        { label: 'Legacy: CA Farm Payroll', icon: Truck, path: '/setup/ca-farm-payroll' },
        { label: 'Accounting Controls', icon: FileCheck, path: '/accounting-controls' },
        { isDivider: true },
        { label: 'License Management', icon: Shield, path: '/admin/licensing' },
        { label: 'Pricing & Plans', icon: DollarSign, path: '/admin/pricing', isHighlight: true },
      ]
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: Wrench,
      children: [
        { label: 'Calculator', icon: Calculator, path: '/tools/calculator' },
        { label: 'Bank Reconciliation', icon: FileText, path: '/tools/bank-reconciliation' },
        { label: 'Scanner', icon: ScanLine, path: '/tools/scanner' },
      ]
    },
    {
      id: 'help',
      label: 'Help',
      icon: HelpCircle,
      children: [
        { label: 'Help Center', icon: HelpCircle, path: '/help/center' },
        { label: 'System Diagnostics', icon: Stethoscope, path: '/help/diagnostics' },
        { label: 'AI Troubleshoot', icon: Sparkles, path: '/help/ai-troubleshoot', isAI: true },
      ]
    },
  ];

  const handleMenuClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setOpenMenu(null);
    } else if (item.children) {
      setOpenMenu(openMenu === item.id ? null : item.id);
    }
  };

  const handleItemClick = (path) => {
    navigate(path);
    setOpenMenu(null);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-slate-800 text-white h-10 flex items-center px-2">
      {menuItems.map((item) => (
        <div key={item.id} className="relative">
          <button
            onClick={() => handleMenuClick(item)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors
              ${isActive(item.path || `/${item.id}`) ? 'bg-slate-700 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}
            `}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
            {item.children && <ChevronDown className="w-3 h-3 ml-0.5" />}
          </button>

          {/* Dropdown Menu */}
          {item.children && openMenu === item.id && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
              <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                {item.children.map((child, idx) => (
                  child.isDivider ? (
                    <div key={idx} className="border-t border-gray-100 my-1" />
                  ) : (
                    <button
                      key={idx}
                      onClick={() => handleItemClick(child.path)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors
                        ${isActive(child.path) ? 'bg-sky-50 text-sky-700' : 'text-gray-700 hover:bg-gray-50'}
                        ${child.isAI ? 'text-purple-600' : ''}
                        ${!isPathLicensed(child.path) ? 'opacity-50' : ''}
                      `}
                      data-testid={`nav-item-${child.path?.replace(/\//g, '-')}`}
                    >
                      <child.icon className={`w-4 h-4 ${child.isAI ? 'text-purple-500' : 'text-gray-400'}`} />
                      <span>{child.label}</span>
                      {child.isAI && <Sparkles className="w-3 h-3 text-purple-400 ml-auto" />}
                      {child.isHighlight && !isPathLicensed(child.path) && <Lock className="w-3 h-3 text-slate-400 ml-auto" />}
                    </button>
                  )
                ))}
              </div>
            </>
          )}
        </div>
      ))}
    </nav>
  );
};

export default HorizontalNav;
